import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";

import TeamModel from "../../models/TeamModel";
import TeamMemberModel from "../../models/TeamMemberModel";
import Loading from "../shared/Loading";
import { getAuthHeader } from "../../helpers/authHelper";
import { validateEmail } from "../../helpers/emailHelper";

const EditTeamMembers = () => {
    const { teamId } = useParams();
    const navigate = useNavigate();
    const [team, setTeam] = useState<TeamModel>();
    const [members, setMembers] = useState<TeamMemberModel[]>([]);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch("/api/Admin/GetTeams", {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: getAuthHeader(),
            },
        })
            .then((response) => response.json())
            .then((json: TeamModel[]) => {
                const found = json.find((t) => t.id === parseInt(teamId!));
                if (found) {
                    setTeam(found);
                    setMembers(found.teamMembers.map((m) => ({
                        ...m,
                        phoneNumber: formatPhoneNumber(m.phoneNumber),
                    })));
                }
                setLoading(false);
            });
    }, [teamId]);

    const handleMemberChange = (index: number, field: string, value: string | boolean) => {
        const updated = [...members];
        (updated[index] as any)[field] = value;
        setMembers(updated);
    };

    const handleTeamLeaderToggle = (index: number) => {
        const updated = members.map((m, i) => ({
            ...m,
            isTeamLeader: i === index,
        }));
        setMembers(updated);
    };

    const getEmailError = (member: TeamMemberModel): string => {
        if (!member.email || member.email.trim().length === 0) {
            return "";
        }
        const result = validateEmail(member.email);
        if (!result || result.length === 0) {
            return "Email is invalid";
        }
        return "";
    };

    const formatPhoneNumber = (phoneNumber: string | undefined | null): string => {
        if (!phoneNumber) return "";
        const cleaned = phoneNumber.replace(/\D/g, "");
        if (cleaned.length >= 10) {
            return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6, 10)}`;
        }
        return cleaned;
    };

    const normalizePhoneNumber = (phoneNumber: string | undefined | null): string => {
        if (!phoneNumber) return "";
        let normalized = phoneNumber.replace(/\D/g, "");
        if (normalized.length === 11 && normalized.startsWith("1")) {
            normalized = normalized.substring(1);
        }
        return normalized;
    };

    const validatePhoneNumber = (phoneNumber: string | undefined | null): boolean => {
        if (!phoneNumber || phoneNumber.trim().length === 0) return true;
        const cleaned = phoneNumber.replace(/\D/g, "");
        return cleaned.length === 10;
    };

    const handlePhoneBlur = (index: number) => {
        const updated = [...members];
        const raw = updated[index].phoneNumber;
        if (raw && raw.trim().length > 0) {
            const normalized = normalizePhoneNumber(raw);
            updated[index] = { ...updated[index], phoneNumber: formatPhoneNumber(normalized) };
            setMembers(updated);
        }
    };

    const getPhoneError = (member: TeamMemberModel): string => {
        if (!member.phoneNumber || member.phoneNumber.trim().length === 0) {
            return "";
        }
        if (!validatePhoneNumber(member.phoneNumber)) {
            return "Phone number must be 10 digits";
        }
        return "";
    };

    const hasValidationErrors = (): boolean => {
        return members.some((m) => getEmailError(m) !== "" || getPhoneError(m) !== "");
    };

    const handleSave = async () => {
        setSaving(true);

        const body = {
            id: team!.id,
            teamMembers: members.map((m) => {
                const { fullName, ...rest } = m;
                return {
                    ...rest,
                    phoneNumber: normalizePhoneNumber(m.phoneNumber),
                };
            }),
        };

        const response = await fetch("/api/Admin/UpdateTeamMembers", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: getAuthHeader(),
            },
            body: JSON.stringify(body),
        });

        const json = await response.json();
        setSaving(false);

        if (json.success) {
            toast.success("Team members updated!", { duration: 5000 });
            navigate("/admin/team-info");
        } else {
            toast.error("Error updating team members: " + json.message, {
                duration: 5000,
            });
        }
    };

    if (loading) {
        return (
            <div className="m-2">
                <Loading />
            </div>
        );
    }

    if (!team) {
        return <h2>Team not found</h2>;
    }

    return (
        <div className="py-lg-3 py-md-2 py-sm-2 px-2">
            <div className="flex space-between align-center mb-3">
                <h2 style={{ marginBottom: 0 }}>
                    Edit Members - Team #{team.teamNumber}
                </h2>
            </div>
            <div className="row">
                {members.map((member, index) => (
                    <div key={member.id} className="col-12 col-lg-6 pb-4">
                        <div className="card" style={{ height: "100%" }}>
                            <div className="card-header">
                                <div className="h5 card-title" style={{ marginBottom: 0 }}>
                                    Player #{index + 1}{" "}
                                    {member.isTeamLeader && <span>(Team Leader)</span>}
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-6">
                                        <div className="form-floating my-2">
                                            <input
                                                className="form-control"
                                                value={member.firstName}
                                                onChange={(e) =>
                                                    handleMemberChange(index, "firstName", e.target.value)
                                                }
                                            />
                                            <label className="form-label">First Name</label>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="form-floating my-2">
                                            <input
                                                className="form-control"
                                                value={member.lastName}
                                                onChange={(e) =>
                                                    handleMemberChange(index, "lastName", e.target.value)
                                                }
                                            />
                                            <label className="form-label">Last Name</label>
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-12">
                                        <div className="form-floating my-2">
                                            <input
                                                className="form-control"
                                                type="email"
                                                value={member.email}
                                                onChange={(e) =>
                                                    handleMemberChange(index, "email", e.target.value)
                                                }
                                            />
                                            <label className="form-label">Email</label>
                                        </div>
                                        {getEmailError(member) && <div className="required-input">
                                            {getEmailError(member)}
                                        </div>}
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-12">
                                        <div className="form-floating my-2">
                                            <input
                                                className="form-control"
                                                type="text"
                                                value={member.phoneNumber ?? ""}
                                                onChange={(e) =>
                                                    handleMemberChange(index, "phoneNumber", e.target.value)
                                                }
                                                onBlur={() => handlePhoneBlur(index)}
                                            />
                                            <label className="form-label">Phone Number</label>
                                        </div>
                                        {getPhoneError(member) && <div className="required-input">
                                            {getPhoneError(member)}
                                        </div>}
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-12">
                                        <div className="form-floating my-2">
                                            <select
                                                className="form-select"
                                                value={member.shirtSize ?? ""}
                                                onChange={(e) =>
                                                    handleMemberChange(index, "shirtSize", e.target.value)
                                                }
                                            >
                                                <option value=""></option>
                                                <option value="Small">Small</option>
                                                <option value="Medium">Medium</option>
                                                <option value="Large">Large</option>
                                                <option value="XL">XL</option>
                                                <option value="XXL">XXL</option>
                                                <option value="XXXL">XXXL</option>
                                            </select>
                                            <label htmlFor="floatingSelect">T-Shirt Size</label>
                                        </div>
                                    </div>
                                </div>
                                <div className="form-check form-switch mt-2">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        checked={member.isTeamLeader}
                                        onChange={() => handleTeamLeaderToggle(index)}
                                    />
                                    <label className="form-check-label">Team Leader</label>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <div className="mt-2">
                <button
                    className="btn btn-success me-2"
                    onClick={handleSave}
                    disabled={saving || hasValidationErrors()}
                >
                    {saving ? "Saving..." : "Save Changes"}
                </button>
                <button
                    className="btn btn-secondary"
                    onClick={() => navigate("/admin/team-info")}
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};

export default EditTeamMembers;
